import { useState } from 'react';
import { ImageIcon, Loader2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import type { IRegenerateMediaModalProps } from '@/@types/sceneEditorV2';

// ---------------------------------------------------------------------------
// Internal sub-components (not exported)
// ---------------------------------------------------------------------------

interface IMediaSectionProps {
  label: string;
  imageUrl: string | null;
  prompt: string;
  onRegenerate: (prompt: string) => void;
  isRegenerating: boolean;
}

function MediaSection({
  label,
  imageUrl,
  prompt,
  onRegenerate,
  isRegenerating,
}: IMediaSectionProps) {
  const [editedPrompt, setEditedPrompt] = useState(() => prompt);

  return (
    <div className="rounded-lg border border-border p-4">
      <h4 className="mb-2 text-sm font-medium">{label}</h4>
      <div className="flex gap-4">
        {/* Image preview */}
        <div className="h-[240px] w-[135px] flex-shrink-0 overflow-hidden rounded-md border border-border">
          {isRegenerating ? (
            <Skeleton className="h-full w-full" />
          ) : imageUrl ? (
            <img src={imageUrl} alt={label} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-accent">
              <ImageIcon size={20} className="text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Prompt + regenerate */}
        <div className="flex flex-1 flex-col gap-2">
          <Textarea
            value={editedPrompt}
            onChange={e => setEditedPrompt(e.target.value)}
            className="min-h-[80px] text-xs"
            placeholder="Image generation prompt..."
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRegenerate(editedPrompt)}
            disabled={isRegenerating}
            className="self-end"
          >
            {isRegenerating ? (
              <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
              <RefreshCw size={14} className="mr-1" />
            )}
            Regenerate
          </Button>
        </div>
      </div>
    </div>
  );
}

interface IVideoSectionProps {
  videoUrl: string;
  prompt: string;
  onRegenerate: (prompt: string) => void;
  isRegenerating: boolean;
}

function VideoSection({ videoUrl, prompt, onRegenerate, isRegenerating }: IVideoSectionProps) {
  const [editedPrompt, setEditedPrompt] = useState(() => prompt);

  return (
    <div className="rounded-lg border border-border p-4">
      <h4 className="mb-2 text-sm font-medium">Video</h4>
      <div className="flex gap-4">
        {/* Video preview */}
        <div className="h-[240px] w-[135px] flex-shrink-0 overflow-hidden rounded-md border border-border">
          {isRegenerating ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <video src={videoUrl} className="h-full w-full object-cover" />
          )}
        </div>

        {/* Prompt + regenerate */}
        <div className="flex flex-1 flex-col gap-2">
          <Textarea
            value={editedPrompt}
            onChange={e => setEditedPrompt(e.target.value)}
            className="min-h-[80px] text-xs"
            placeholder="Video generation prompt..."
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRegenerate(editedPrompt)}
            disabled={isRegenerating}
            className="self-end"
          >
            {isRegenerating ? (
              <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
              <RefreshCw size={14} className="mr-1" />
            )}
            Regenerate
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal component
// ---------------------------------------------------------------------------

export function RegenerateMediaModal({
  open,
  onClose,
  firstFrameUrl,
  lastFrameUrl,
  videoUrl,
  firstFramePrompt,
  lastFramePrompt,
  videoPrompt,
  onRegenerateImage,
  onRegenerateVideo,
  isRegeneratingFirstFrame,
  isRegeneratingLastFrame,
  isRegeneratingVideo,
}: IRegenerateMediaModalProps) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-h-[95vh] sm:max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Regenerate Media</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* First Frame Section — key resets local prompt state when prop changes */}
          <MediaSection
            key={`first-${firstFramePrompt}`}
            label="First Frame"
            imageUrl={firstFrameUrl}
            prompt={firstFramePrompt}
            onRegenerate={prompt => onRegenerateImage('first', prompt)}
            isRegenerating={isRegeneratingFirstFrame}
          />

          {/* Last Frame Section (only if scene uses 2 frames) */}
          {lastFrameUrl !== null && (
            <MediaSection
              key={`last-${lastFramePrompt}`}
              label="Last Frame"
              imageUrl={lastFrameUrl}
              prompt={lastFramePrompt}
              onRegenerate={prompt => onRegenerateImage('last', prompt)}
              isRegenerating={isRegeneratingLastFrame}
            />
          )}

          {/* Video Section (only if video exists) */}
          {videoUrl !== null && (
            <VideoSection
              key={`video-${videoPrompt}`}
              videoUrl={videoUrl}
              prompt={videoPrompt}
              onRegenerate={onRegenerateVideo}
              isRegenerating={isRegeneratingVideo}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
